import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { MaIcon } from "../../components/ui";
import { useAuthStatus, useBootstrapAdmin } from "../../lib/queries";

/**
 * One-shot screen to promote the currently signed-in user to admin.
 * Reachable at /admin/bootstrap. The Worker endpoint is unauthenticated
 * (it checks ADMIN_BOOTSTRAP_KEY instead) but we still require auth on
 * this route so we know the userId — passing the key with the wrong
 * userId would be a footgun.
 *
 * After this once-per-team handshake, further admin promotions happen
 * in-app at /admin/users — no more curl, no more bootstrap key.
 */
export function AdminBootstrap() {
  const navigate = useNavigate();
  const { user } = useAuthStatus();
  const bootstrap = useBootstrapAdmin();
  const [key, setKey] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [ok, setOk] = useState(false);

  // If they're already admin, send them straight to the console.
  if (user?.role === "admin") {
    return (
      <div className="ma-screen" style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: 32, textAlign: "center" }}>
        <div className="ma-display" style={{ fontSize: 26, marginBottom: 8 }}>Already an admin</div>
        <div style={{ fontFamily: "var(--ma-body)", fontSize: 14, color: "var(--ma-ink-soft)", marginBottom: 18 }}>
          You're <strong>{user.name}</strong> ({user.email}). No bootstrap needed.
        </div>
        <button onClick={() => navigate("/admin")} style={primaryBtn}>open admin console →</button>
      </div>
    );
  }

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!key.trim()) { setError("Paste the ADMIN_BOOTSTRAP_KEY first."); return; }
    if (!user) { setError("No user — sign in first."); return; }
    try {
      await bootstrap.mutateAsync({ key: key.trim(), userId: user.id });
      setOk(true);
      // Small delay so the success state is visible, then go to /admin.
      // The ["me"] invalidate in the mutation onSuccess will have rolled
      // role → "admin" by the time the route guard checks.
      setTimeout(() => navigate("/admin"), 600);
    } catch (err: any) {
      const msg = String(err?.message ?? err);
      if (msg.includes("403") || msg.includes("forbidden")) {
        setError("Wrong key. Double-check the ADMIN_BOOTSTRAP_KEY Worker secret.");
      } else {
        setError(msg.slice(0, 200));
      }
    }
  };

  return (
    <div className="ma-screen">
      <div style={{
        padding: "calc(env(safe-area-inset-top, 20px) + 28px) 24px 0",
        display: "flex", justifyContent: "space-between", alignItems: "center",
      }}>
        <button onClick={() => navigate(-1)} style={{
          background: "transparent", border: "none", padding: 0,
          color: "var(--ma-ink)", fontFamily: "var(--ma-mono)", fontSize: 11,
          letterSpacing: "0.14em", textTransform: "uppercase", cursor: "pointer",
        }}>← Back</button>
      </div>

      <div style={{ padding: "32px 24px 0" }}>
        <div className="ma-mono" style={{
          fontSize: 10, letterSpacing: "0.2em", textTransform: "uppercase",
          color: "var(--ma-brick)", marginBottom: 10,
        }}>one-shot bootstrap</div>
        <div className="ma-display" style={{ fontSize: 36, lineHeight: 0.95, color: "var(--ma-ink)", marginBottom: 14 }}>
          Become admin
        </div>
        <div style={{ fontFamily: "var(--ma-body)", fontSize: 14, lineHeight: 1.45, color: "var(--ma-ink-soft)", marginBottom: 18 }}>
          Paste the <code style={{ fontFamily: "var(--ma-mono)", fontSize: 13 }}>ADMIN_BOOTSTRAP_KEY</code> Worker secret to promote{" "}
          {user ? <strong style={{ color: "var(--ma-ink)" }}>{user.email}</strong> : "your account"} to admin.
          This is a one-time step per team — afterwards, further admins are added inside <code style={{ fontFamily: "var(--ma-mono)", fontSize: 13 }}>/admin/users</code>.
        </div>

        <div style={{
          background: "var(--ma-cream)",
          border: "var(--ma-border) solid var(--ma-ink)",
          borderRadius: "var(--ma-card-radius)",
          padding: "12px 14px", marginBottom: 20,
        }}>
          <div className="ma-mono" style={{ fontSize: 10, letterSpacing: "0.16em", textTransform: "uppercase", color: "var(--ma-ink-soft)", marginBottom: 6 }}>
            don't have a key yet?
          </div>
          <div style={{ fontFamily: "var(--ma-body)", fontSize: 13, lineHeight: 1.4, color: "var(--ma-ink)" }}>
            Run from your local machine (one line):
            <pre style={{
              marginTop: 6, padding: "8px 10px", borderRadius: 4,
              background: "var(--ma-paper)",
              border: "1px dashed var(--ma-line)",
              fontFamily: "var(--ma-mono)", fontSize: 11,
              overflowX: "auto", whiteSpace: "pre",
            }}>{`wrangler secret put ADMIN_BOOTSTRAP_KEY --cwd apps/api`}</pre>
            …then paste whatever string you typed when prompted.
          </div>
        </div>

        <form onSubmit={onSubmit}>
          <label style={{ display: "block", marginBottom: 16 }}>
            <div className="ma-mono" style={{
              fontSize: 10, letterSpacing: "0.16em",
              textTransform: "uppercase", color: "var(--ma-ink-soft)", marginBottom: 6,
            }}>Bootstrap key</div>
            <input
              type="password"
              value={key}
              onChange={(e) => setKey(e.target.value)}
              placeholder="paste here"
              autoFocus
              autoComplete="off"
              style={{
                width: "100%", padding: "14px",
                background: "var(--ma-cream)",
                border: "var(--ma-border) solid var(--ma-ink)",
                borderRadius: "var(--ma-card-radius)",
                fontFamily: "var(--ma-mono)", fontSize: 16,
                color: "var(--ma-ink)", boxSizing: "border-box",
                letterSpacing: "0.08em",
              }}
            />
          </label>

          <button
            type="submit"
            disabled={bootstrap.isPending || ok || !user}
            style={{
              width: "100%", padding: "16px 0",
              background: ok ? "var(--ma-canal)" : "var(--ma-ink)",
              color: "var(--ma-paper)",
              border: "none", borderRadius: "var(--ma-card-radius)",
              fontFamily: "var(--ma-display)", fontWeight: 700, fontSize: 16,
              letterSpacing: "0.02em", cursor: (bootstrap.isPending || ok) ? "default" : "pointer",
              display: "flex", alignItems: "center", justifyContent: "center", gap: 10,
              opacity: bootstrap.isPending ? 0.6 : 1,
            }}>
            {ok ? "✓ promoted — opening admin…"
              : bootstrap.isPending ? "promoting…"
              : "Promote me"}
            {!ok && !bootstrap.isPending && (
              <MaIcon name="arrow" size={16} stroke={2} color="var(--ma-paper)" />
            )}
          </button>

          {error && (
            <div className="ma-mono" style={{
              fontSize: 11, color: "var(--ma-brick)", marginTop: 12,
              textTransform: "uppercase", letterSpacing: "0.12em",
            }}>
              {error}
            </div>
          )}
        </form>
      </div>
    </div>
  );
}

const primaryBtn: React.CSSProperties = {
  padding: "12px 22px",
  background: "var(--ma-ink)", color: "var(--ma-paper)",
  border: "none", borderRadius: "var(--ma-card-radius)",
  fontFamily: "var(--ma-mono)", fontSize: 12, letterSpacing: "0.12em",
  textTransform: "uppercase", fontWeight: 600, cursor: "pointer",
};
